import type { ClientConfigRepository } from '@/core/data/config/clientConfig.repository';
import type { StorageRepository } from '@/core/data/storage/storage.repository';
import { createLogger } from '@/core/helpers/logger.helper';
import type { IntentResultOptions, RespondSparkInvitationDto, SparkApi } from '@/core/network/api/spark/spark.api';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import { HandleApiError, type HttpError } from '@/core/network/http/httpError';
import type {
  CreateIntentDto,
  CreateProposalDto,
  SegmentProposal,
  SegmentResult,
  SparkIntent,
  SparkIntentListItem,
  SparkPrioritizedIntentResults,
  SparkSegment,
  SparkWeekPreview,
  UpdateIntentAvailabilityByDateDto,
  UpdateIntentAvailabilityHorizonDto,
} from './spark.interface';

const logger = createLogger('Spark');

export class SparkRepository {
  private readonly httpAPI: SparkApi;
  private readonly storage: StorageRepository;
  private readonly config: ClientConfigRepository;

  constructor(httpAPI: SparkApi, storage: StorageRepository, config: ClientConfigRepository) {
    this.httpAPI = httpAPI;
    this.storage = storage;
    this.config = config;
  }

  async createIntent(payload: CreateIntentDto): Promise<GamResponse<SparkIntent> | null> {
    try {
      const response = await this.httpAPI.createIntent(payload);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async updateIntent(intentId: string, payload: CreateIntentDto): Promise<GamResponse<SparkIntent> | null> {
    try {
      const response = await this.httpAPI.updateIntent(intentId, payload);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async markProposalSuccessAsRead(proposalId: string): Promise<GamResponse<SegmentProposal> | null> {
    try {
      const response = await this.httpAPI.markProposalSuccessAsRead(proposalId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async updateIntentAvailabilityByDate(
    dateString: string,
    payload: UpdateIntentAvailabilityByDateDto,
  ): Promise<GamResponse<SparkIntent> | null> {
    try {
      const response = await this.httpAPI.updateIntentAvailabilityByDate(dateString, payload);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async updateIntentAvailabilityHorizon(
    payload: UpdateIntentAvailabilityHorizonDto,
  ): Promise<GamResponse<SparkIntent[]> | null> {
    try {
      const response = await this.httpAPI.updateIntentAvailabilityHorizon(payload);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async initializeAvailabilityHorizon(cityId: string): Promise<GamResponse<SparkIntent[]> | null> {
    try {
      const response = await this.httpAPI.initializeAvailabilityHorizon(cityId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async deleteIntent(intentId: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.deleteIntent(intentId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createProposal(payload: CreateProposalDto): Promise<GamResponse<SegmentProposal> | null> {
    try {
      const response = await this.httpAPI.createProposal(payload);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createProposalInvitations(proposalId: string, userIds: string[]): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.createProposalInvitations(proposalId, userIds);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async acceptProposalInvitation(
    proposalId: string,
    chessSetCount?: number,
  ): Promise<GamResponse<SegmentProposal> | null> {
    try {
      const response = await this.httpAPI.acceptProposalInvitation(proposalId, chessSetCount);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async declineProposalInvitation(proposalId: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.declineProposalInvitation(proposalId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async withdrawProposalInvitation(proposalId: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.withdrawProposalInvitation(proposalId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getRespondSparkInvitations(): Promise<GamResponse<RespondSparkInvitationDto[]> | null> {
    try {
      const response = await this.httpAPI.getRespondSparkInvitations();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getProposal(id: string): Promise<GamResponse<SegmentProposal> | null> {
    try {
      const response = await this.httpAPI.getProposal(id);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async createConfirmation(proposalId: string, intentId: string): Promise<GamResponse<SegmentProposal> | null> {
    try {
      const response = await this.httpAPI.createConfirmation(proposalId, intentId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async deleteConfirmation(proposalId: string): Promise<GamResponse<SegmentProposal> | null> {
    try {
      const response = await this.httpAPI.deleteConfirmation(proposalId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async updateConfirmationChessSetCount(
    proposalId: string,
    chessSetCount: number,
  ): Promise<GamResponse<SegmentProposal> | null> {
    try {
      const response = await this.httpAPI.updateConfirmationChessSetCount(proposalId, chessSetCount);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async meetAnyways(proposalId: string): Promise<GamResponse<SegmentProposal> | null> {
    try {
      const response = await this.httpAPI.meetAnyways(proposalId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async undoMeetAnyways(proposalId: string): Promise<GamResponse<SegmentProposal> | null> {
    try {
      const response = await this.httpAPI.undoMeetAnyways(proposalId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getIntent(intentId: string): Promise<GamResponse<SparkIntent> | null> {
    try {
      const response = await this.httpAPI.getIntent(intentId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getIntentByDate(dateString: string): Promise<GamResponse<SparkIntent> | null> {
    try {
      const response = await this.httpAPI.getIntentByDate(dateString);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getWeekPreview(from: string): Promise<GamResponse<SparkWeekPreview> | null> {
    try {
      const response = await this.httpAPI.getWeekPreview(from);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getIntents(): Promise<GamResponse<SparkIntentListItem[]> | null> {
    try {
      const response = await this.httpAPI.getIntents();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async extendAvailabilities(cityId: string): Promise<GamResponse<SparkIntent[]> | null> {
    try {
      const response = await this.httpAPI.extendAvailabilities(cityId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async findMatches(locationIds: string[], start: Date, end: Date): Promise<GamResponse<SparkSegment[]> | null> {
    try {
      const response = await this.httpAPI.findMatches(locationIds, start, end);
      if (response.data?.data) {
        return {
          data: response.data.data,
          cursor: response.data.cursor,
        };
      } else {
        return null;
      }
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async findIntentResults(
    intentId: string,
    options?: IntentResultOptions,
  ): Promise<GamResponse<SegmentResult[]> | null> {
    try {
      const response = await this.httpAPI.findIntentResults(intentId, options);
      if (response.data?.data) {
        return {
          data: response.data.data,
          cursor: response.data.cursor,
        };
      } else {
        return null;
      }
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async findPrioritizedIntentResults(
    intentId: string,
    gpsCoords?: { lat: number; lng: number },
  ): Promise<GamResponse<SparkPrioritizedIntentResults> | null> {
    try {
      const response = await this.httpAPI.findPrioritizedIntentResults(intentId, gpsCoords);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createFirestoreProposalDoc(id: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.createFirestoreProposalDoc(id);
      return response.status === 200 ? response.data || { data: response.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createFirestoreIntentDoc(id: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.createFirestoreIntentDoc(id);
      return response.status === 200 ? response.data || { data: response.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }
}
